import React, { useEffect, useState } from 'react';
import { X, Calendar, Clock, Check, Info, CalendarDays, BookOpen, Link2, Loader } from 'lucide-react';
import axios from 'axios';
import { useEnroll } from 'Context/enrollContext';
import { useAuth } from 'Context/authContext';
import constants from '../../constants';

interface BookingPopupProps {
  isOpen: boolean;
  onClose: () => void;
  // onBooking: (date: string, timeSlot: string, name: string, email: string) => void;
}

interface Meeting {
  _id: string;
  startTime: string;
  endTime: string;
  description: string;
  meetLink?: string;
  recordingLink?: string;
  remainingEnrollment:any
}

interface SessionData {
  state: "NEWSLOTS" | "UPCOMING" | "MAXLIMIT" |null;
  doc: Meeting[];
  previousBookedMeetings?: Meeting[];
}

const BookingPopup: React.FC<BookingPopupProps> = ({ isOpen, onClose }) => {
  const { getToken } = useAuth();
  const { enroll } = useEnroll();
  const [selectedDate, setSelectedDate] = useState<Meeting | null>(null);
  const [data, setData] = useState<SessionData | null>(null);
  const [status, setStatus] = useState<string | null>(null);
  const [taskStep, setTaskStep] = useState<'1' | '2'>('1');
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [isActionLoading, setIsActionLoading] = useState<boolean>(false);

  // Fixed time slot for all bookings
  const fixedTimeSlot = "9:00 PM";

  const fetchActiveSlots = async () => {
    if (!isOpen || !enroll?._id) return;
    
    setIsLoading(true);
    try {
      const token = await getToken();
      const response = await axios.get(`${constants.DOMAIN}/api/v1/meeting/getActiveSlots/${enroll._id}`, {
        headers: {
          Authorization: `Bearer ${token}`
        }
      });
      setData(response.data.data);
      setStatus(null);
    } catch (error: any) {
      console.error('Error fetching active slots:', error.response ? error.response.data : error.message);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (isOpen) {
      fetchActiveSlots();
      setSelectedDate(null);
    }
  }, [isOpen, enroll?._id]);

  const handleDateSelect = (date: Meeting) => {
    setSelectedDate(date);
  };

  const handleNextStep = async () => {
    if (isActionLoading) return;
    
    setIsActionLoading(true);
    try {
      const token = await getToken();
      
      if (data?.state === "NEWSLOTS" && selectedDate) {
        await axios.post(`${constants.DOMAIN}/api/v1/meeting/addBooking`, {
          enrollmentId: enroll._id,
          meetingId: selectedDate._id
        }, {
          headers: {
            Authorization: `Bearer ${token}`
          }
        });
        setStatus('SUCCESS');
      }
      
      if (data?.state === "UPCOMING" && data.doc[0]?._id) {
        await axios.post(`${constants.DOMAIN}/api/v1/meeting/deleteBooking`, {
          enrollmentId: enroll._id,
          meetingId: data.doc[0]._id
        }, {
          headers: {
            Authorization: `Bearer ${token}`
          }
        });
        await fetchActiveSlots();
      }
    } catch (error) {
      console.error('Action failed:', error);
      setStatus('ERROR');
    } finally {
      setIsActionLoading(false);
    }
  };

  const handleReset = () => {
    setSelectedDate(null);
  };

  const formatDate = (isoString: string) => {
    const date = new Date(isoString);
    const options: Intl.DateTimeFormatOptions = { year: 'numeric', month: 'long', day: '2-digit', weekday: 'long' };
    const formattedDate = date.toLocaleDateString('en-US', options);
    
    // Rearrange the format to match: "Month DD, YYYY ( Weekday )"
    const [weekday, month, day, year] = formattedDate.split(' ');
    return `${month} ${day.replace(',', '')}, ${year} ( ${weekday.split(',')[0]} )`;
  };

  const formatTime = (isoString: string) => {
    return new Date(isoString).toLocaleTimeString('en-US', { 
      hour: '2-digit', 
      minute: '2-digit', 
      hour12: true, 
      // timeZone: 'UTC'
    });
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-90 flex items-center justify-center z-[9999] p-4 backdrop-blur-sm overflow-y-scroll">
      <div className="bg-white rounded-lg mt-24 phone:mt-0 shadow-xl w-full max-w-4xl overflow-hidden flex flex-col md:flex-row">
        {/* Left side - Meeting info */}
        <div className="bg-gradient-to-br from-brand to-indigo-800 text-white p-6 md:w-2/5">
          <div className="h-full flex flex-col">
            <h2 className="text-2xl font-bold mb-4">Live Doubt Session</h2>
            <div className="mb-6">
              <div className="flex items-start mb-3">
                <Clock className="h-5 w-5 mr-3 mt-0.5 flex-shrink-0" />
                <div>
                  <p className="font-medium">60 Minutes</p>
                  <p className="text-blue-100 text-sm">General time: {fixedTimeSlot}</p>
                </div>
              </div>
              <div className="flex items-start mb-3">
                <Calendar className="h-5 w-5 mr-3 mt-0.5 flex-shrink-0" />
                <div>
                  <p className="font-medium">Select a Date</p>
                  <p className="text-blue-100 text-sm">Available dates for the next 3 slots</p>
                </div>
              </div>
              <div className="hidden phone:flex items-start">
                <Info className="h-5 w-5 mr-3 mt-0.5 flex-shrink-0" />
                <div>
                  <p className="font-medium">Meeting Description</p>
                  <p className="text-blue-100 text-sm mt-1">
                    Join this interactive doubt-clearing session where you can ask questions, clarify concepts, and get personalized guidance on your topics of concern. Our mentor will address your queries, provide in-depth explanations, and ensure you leave with a clear understanding.
                  </p>
                </div>
              </div>
            </div>
            {/* <div className="mt-auto">
              <p className="text-sm text-blue-100 italic">
                "Internship is the passport to the future, for tomorrow belongs to those who prepare for it today."
              </p>
            </div> */}
          </div>
        </div>

        {/* Right side - Booking form */}
        <div className="p-6 md:w-3/5">
          <div className="flex justify-between items-center mb-6">
            <h2 className="text-xl font-semibold text-gray-800">
              {isLoading ? 'Loading...' : 
                data?.state === "NEWSLOTS" ? 'Select a Date' : 
                data?.state === "UPCOMING" ? 'Upcoming Session' :'Max Limit'}
            </h2>
            <button 
              onClick={onClose}
              className="text-gray-500 hover:text-gray-700 transition-colors"
              disabled={isActionLoading}
            >
              <X className="h-5 w-5" />
            </button>
          </div>

          {isLoading ? (
            <div className="flex flex-col items-center justify-center py-12">
              <Loader className="h-8 w-8 text-brand animate-spin mb-4" />
              <p className="text-gray-600">Loading session data...</p>
            </div>
          ) : (
            <>
              {/* New slots and not yet booked */}
              {data?.state === "NEWSLOTS" && status !== "SUCCESS" && (
                <div className="mb-6">
                  <p className="text-gray-600 mb-4">
                    Please select a date for your doubt session.
                  </p>
                  <div className="space-y-4">
                    {data.doc?.map((update) => (
                      <div 
                        key={update._id}
                        className={`${selectedDate?.startTime === update.startTime ? "bg-brand text-white" : "bg-white hover:bg-gray-100"} rounded-lg shadow-lg p-4 cursor-pointer transform transition-all duration-300 border border-gray-300`}
                        onClick={() => handleDateSelect(update)}
                      >
                        <div className="flex items-start space-x-3">
                          <div className="flex-shrink-0">
                            <CalendarDays className={selectedDate?.startTime === update.startTime ? "text-white" : ""} />
                          </div>
                          <div className="flex-1">
                            <p className={`text-sm font-semibold ${selectedDate?.startTime === update.startTime ? "text-white" : "text-brand"} mb-1`}>
                              {formatDate(update.startTime)}
                            </p>
                            <p className={`${selectedDate?.startTime === update.startTime ? "text-white" : "text-black"} text-base flex items-center`}>
                              <Clock height={15} className={selectedDate?.startTime === update.startTime ? "text-white" : ""} /> {formatTime(update.startTime)}
                            </p>
                            <p className={`${selectedDate?.startTime === update.startTime ? "text-white" : "text-black"} text-[12px] flex items-center`}>
                             Available Seats: <span className='font-bold '>{" "} {update.remainingEnrollment + 1}</span>
                            </p>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              {data?.state === "MAXLIMIT"  && (
                <div className="mb-6">
                  <p className="text-gray-600 mb-4">
                    You have already reached max limit of booking. In case of any concern feel free to react out support team of YouVah.
                  </p>
                  <h6 className='py-3'>Previous Booking</h6>
                  <div className="space-y-4">
                    {data.previousBookedMeetings?.map((update) => (
                      <div 
                        key={update._id}
                        className={`${selectedDate?.startTime === update.startTime ? "bg-brand text-white" : "bg-white hover:bg-gray-100"} rounded-lg shadow-lg p-4 cursor-pointer transform transition-all duration-300 border border-gray-300`}
                        onClick={() => handleDateSelect(update)}
                      >
                        <div className="flex items-start space-x-3">
                          <div className="flex-shrink-0">
                            <CalendarDays className={selectedDate?.startTime === update.startTime ? "text-white" : ""} />
                          </div>
                          <div className="flex-1">
                            <p className={`text-sm font-semibold ${selectedDate?.startTime === update.startTime ? "text-white" : "text-brand"} mb-1`}>
                              {formatDate(update.startTime)}
                            </p>
                            <p className={`${selectedDate?.startTime === update.startTime ? "text-white" : "text-black"} text-base flex items-center`}>
                              <Clock height={15} className={selectedDate?.startTime === update.startTime ? "text-white" : ""} /> {formatTime(update.startTime)}
                            </p>
                           
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              {/* Booking confirmed */}
              {status === "SUCCESS" && (
                <div className="max-w-lg mx-auto rounded-lg overflow-hidden">
                  <div className="px-6 py-4">
                    <h2 className="text-xl font-bold text-green-600">Booking Confirmed!</h2>
                  </div>
                  <div className="p-6">
                    <div className="mb-6">
                      <p className="text-gray-600 mb-4">
                        Your doubt session has been scheduled. You'll receive a confirmation email shortly.
                      </p>
                      <div className="bg-gray-50 p-4 rounded-md">
                        <div className="flex items-center mb-3">
                          <Calendar className="h-5 w-5 text-green-600 mr-3" />
                          <div>
                            <p className="text-sm text-gray-500">Date</p>
                            <p className="font-medium">{selectedDate ? formatDate(selectedDate.startTime) : ''}</p>
                          </div>
                        </div>
                        <div className="flex items-center mb-3">
                          <Clock className="h-5 w-5 text-green-600 mr-3" />
                          <div>
                            <p className="text-sm text-gray-500">Time</p>
                            <p className="font-medium">{selectedDate ? formatTime(selectedDate.startTime) : ''}</p>
                          </div>
                        </div>
                      </div>
                    </div>
                    <button
                      onClick={() => {
                        setStatus(null);
                        fetchActiveSlots();
                      }}
                      className="w-full px-4 py-2 bg-brand text-white rounded-md hover:bg-blue-700 transition-colors"
                    >
                      Back
                    </button>
                  </div>
                </div>
              )}

              {/* Booking error */}
              {status === "ERROR" && (
                <div className="max-w-lg mx-auto rounded-lg overflow-hidden">
                  <div className="px-6 py-4">
                    <h2 className="text-xl font-bold text-red-600">Booking Failed</h2>
                  </div>
                  <div className="p-6">
                    <div className="mb-6">
                      <p className="text-gray-600 mb-4">
                        There was an error processing your booking. Please try again later.
                      </p>
                    </div>
                    <button
                      onClick={() => {
                        setStatus(null);
                        fetchActiveSlots();
                      }}
                      className="w-full px-4 py-2 bg-brand text-white rounded-md hover:bg-blue-700 transition-colors"
                    >
                      Try Again
                    </button>
                  </div>
                </div>
              )}

              {/* Upcoming session view */}
              {data?.state === "UPCOMING" && (
                <div className="mb-6">
                  <div className="w-max mx-auto rounded-lg flex justify-center border-[1px] border-solid">
                    <div className="flex p-1 gap-4 rounded-lg">
                      <span 
                        className={`rounded-lg px-6 py-1 cursor-pointer ${taskStep === '1' ? 'bg-purple-500 text-white' : 'bg-gray-100'}`} 
                        onClick={() => setTaskStep('1')}
                      >
                        Upcoming
                      </span>
                      <span 
                        className={`rounded-lg px-6 py-1 cursor-pointer ${taskStep === '2' ? 'bg-purple-500 text-white' : 'bg-gray-100'}`} 
                        onClick={() => setTaskStep('2')}
                      >
                        Previous
                      </span>
                    </div>
                  </div>
                  
                  <div className="mt-4">
                    {/* Upcoming sessions tab */}
                    {taskStep === '1' && data.doc?.map((update) => (
                      <div key={update._id} className="space-y-4">
                        <div className="flex items-center">
                          <BookOpen className="mr-3 text-blue-600" size={24} />
                          <div>
                            <p className="font-semibold text-gray-700">Session Topic</p>
                            <p className="text-gray-600">{update.description}</p>
                          </div>
                        </div>

                        <div className="flex items-center">
                          <Calendar className="mr-3 text-green-600" size={24} />
                          <div>
                            <p className="font-semibold text-gray-700">Date</p>
                            <p className="text-gray-600">{formatDate(update.startTime)}</p>
                          </div>
                        </div>

                        <div className="flex items-center">
                          <Clock className="mr-3 text-purple-600" size={24} />
                          <div>
                            <p className="font-semibold text-gray-700">Time</p>
                            <p className="text-gray-600">
                              {formatTime(update.startTime)} - {formatTime(update.endTime)}
                            </p>
                          </div>
                        </div>

                        {update.meetLink ? (
                          <div className="flex items-center">
                            <Link2 className="mr-3 text-green-600" size={24} />
                            <div>
                              <p className="font-semibold text-gray-700">Meeting link</p>
                              <a href={update.meetLink} className="text-blue-600">
                                {update.meetLink}
                              </a>
                            </div>
                          </div>
                        ) : (
                          <div className="flex items-center p-4 py-4 mb-2 px-3 text-sm text-yellow-800 rounded-lg bg-yellow-50  " role="alert">
                            <svg className="flex-shrink-0 inline w-4 h-4 me-3" aria-hidden="true" xmlns="http://www.w3.org/2000/svg" fill="currentColor" viewBox="0 0 20 20">
                              <path d="M10 .5a9.5 9.5 0 1 0 9.5 9.5A9.51 9.51 0 0 0 10 .5ZM9.5 4a1.5 1.5 0 1 1 0 3 1.5 1.5 0 0 1 0-3ZM12 15H8a1 1 0 0 1 0-2h1v-3H8a1 1 0 0 1 0-2h2a1 1 0 0 1 1 1v4h1a1 1 0 0 1 0 2Z"/>
                            </svg>
                            <span className="sr-only">Info</span>
                            <div>
                              <span className="font-medium">Disclaimer!</span> Meeting Link will be shared shortly with you
                            </div>
                          </div>
                        )}
                      </div>
                    ))}

                    

                    {/* Previous sessions tab */}
                    {taskStep === '2' && (
                      <>
                        {data.previousBookedMeetings && data.previousBookedMeetings.length > 0 ? (
                          data.previousBookedMeetings.map((update) => (
                            <div 
                              key={update._id}
                              className={`${selectedDate?.startTime === update.startTime ? "bg-brand text-white" : "bg-white hover:bg-gray-100"} rounded-lg shadow-lg p-4 cursor-pointer transform transition-all duration-300 border border-gray-300 mb-4`}
                              onClick={() => handleDateSelect(update)}
                            >
                              <div className="flex items-start space-x-3">
                                <div className="flex-shrink-0">
                                  <CalendarDays className={selectedDate?.startTime === update.startTime ? "text-white" : ""} />
                                </div>
                                <div className="flex-1">
                                  <p className={`text-sm font-semibold ${selectedDate?.startTime === update.startTime ? "text-white" : "text-brand"} mb-1`}>
                                    {formatDate(update.startTime)}
                                  </p>
                                  <p className={`${selectedDate?.startTime === update.startTime ? "text-white" : "text-black"} text-base flex items-center`}>
                                    <Clock height={15} className={selectedDate?.startTime === update.startTime ? "text-white" : ""} /> {formatTime(update.startTime)}
                                  </p>
                                </div>
                              </div>
                              {update?.recordingLink && (
                          <div className="flex items-center">
                            <Link2 className="mr-3 text-green-600" size={24} />
                            <div>
                              <p className="font-semibold text-gray-700">Meeting link</p>
                              <a href={update.recordingLink} className="text-blue-600">
                                {update.recordingLink}
                              </a>
                            </div>
                          </div>)}
                        {/* // ) : (
                        //   <div className="flex items-center p-4 py-4 mb-2 px-3 text-sm text-yellow-800 rounded-lg bg-yellow-50  " role="alert">
                        //     <svg className="flex-shrink-0 inline w-4 h-4 me-3" aria-hidden="true" xmlns="http://www.w3.org/2000/svg" fill="currentColor" viewBox="0 0 20 20">
                        //       <path d="M10 .5a9.5 9.5 0 1 0 9.5 9.5A9.51 9.51 0 0 0 10 .5ZM9.5 4a1.5 1.5 0 1 1 0 3 1.5 1.5 0 0 1 0-3ZM12 15H8a1 1 0 0 1 0-2h1v-3H8a1 1 0 0 1 0-2h2a1 1 0 0 1 1 1v4h1a1 1 0 0 1 0 2Z"/>
                        //     </svg>
                        //     <span className="sr-only">Info</span>
                        //     <div>
                        //       <span className="font-medium">Disclaimer!</span> RecordingLink Link will be shared shortly with you
                        //     </div>
                        //   </div> */}
                      
                            </div>
                          ))
                        ) : (
                          <h6 className="mx-auto mt-20 text-center">No Booking Found</h6>
                        )}
                      </>
                    )}
                  </div>
                </div>
              )}

              {/* Action buttons */}
              {((data?.state === "NEWSLOTS" && status !== "SUCCESS") || (data?.state === "UPCOMING" && taskStep === '1')) && (
                <div className="flex justify-between">
                  {data?.state === "NEWSLOTS" && (
                    <button
                      onClick={handleReset}
                      className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500"
                      disabled={isActionLoading}
                    >
                      Reset
                    </button>
                  )}
                  <button
                    onClick={handleNextStep}
                    disabled={(data?.state === "NEWSLOTS" && !selectedDate) || isActionLoading}
                    className={`px-6 py-2 text-sm font-medium text-white rounded-md flex items-center ${
                      isActionLoading ? 'bg-gray-400 cursor-not-allowed' :
                      (data?.state === "NEWSLOTS" && !selectedDate) ? 'bg-purple-300 cursor-not-allowed' :
                      data?.state === "UPCOMING" ? 'bg-red-400 hover:bg-red-600' : 
                      'bg-purple-600 hover:bg-brand focus:outline-none focus:ring-2 focus:ring-blue-500'
                    }`}
                  >
                    {isActionLoading ? (
                      <>
                        <Loader className="mr-2 h-4 w-4 animate-spin" />
                        {data?.state === "NEWSLOTS" ? 'Processing...' : 'Cancelling...'}
                      </>
                    ) : (
                      <>
                        {data?.state === "NEWSLOTS" && 'Confirm Booking'}
                        {data?.state === "UPCOMING" && 'Cancel Booking'}
                        {data?.state === "NEWSLOTS" && <Check className="ml-1 h-4 w-4" />}
                        {data?.state === "UPCOMING" && <X className="ml-1 h-4 w-4" />}
                      </>
                    )}
                  </button>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default BookingPopup;